/* eslint-disable prefer-destructuring */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Stepper from 'components/common/stepper/Stepper'
import { Form, Formik, useFormikContext } from 'formik'
import {
  EVENT_WIZARD_STEP_LABELS,
  EVENT_WIZARD_STEP_VALIDATION,
  EVENT_WIZARD_TOTAL_STEPS,
  eventInitialValues,
} from 'constants/Events'
import { createEvent, showEvent, updateEvent } from 'containers/events/Api'
import { toast } from 'react-toastify'
import CustomToast from 'components/common/custom-toast/CustomToast'
import { alertTypes } from 'constants/Common'
import { useNavigate, useParams } from 'react-router-dom'
import { CaretLeft, CaretRight } from 'phosphor-react'
import 'containers/events/Events.scss'
import FirstStep from './formSteps/FirstStep'
import LocationTimeStep from './formSteps/LocationTimeStep'
import AgendaStep from './formSteps/AgendaStep'
import SpeakerStep from './formSteps/SpeakerStep'
import GalleryStep from './formSteps/GalleryStep'

const TOTAL_STEPS = EVENT_WIZARD_TOTAL_STEPS
const STEP_LABELS = EVENT_WIZARD_STEP_LABELS

function applyYupErrorsToFormik(formik, validationError) {
  const nextErrors = {}
  if (validationError?.inner?.length) {
    validationError.inner.forEach((e) => {
      if (e.path) nextErrors[e.path] = e.message
    })
  } else if (validationError?.path) {
    nextErrors[validationError.path] = validationError.message
  }
  formik.setErrors(nextErrors)
  Object.keys(nextErrors).forEach((path) => {
    formik.setFieldTouched(path, true, false)
  })
}

function EventWizardBody({ id, step, setStep }) {
  const formik = useFormikContext()

  const stepTitle = STEP_LABELS[step - 1] || 'About'
  const formattedStep = `${step}`.padStart(2, '0')
  const totalStepsLabel = `${TOTAL_STEPS}`.padStart(2, '0')
  const progressPercent = Math.min((step / TOTAL_STEPS) * 100, 100)

  const handleStepPick = useCallback(
    async (targetStep) => {
      if (targetStep < step) {
        formik.setErrors({})
        setStep(targetStep)
        return
      }
      if (targetStep === step) return

      const validateForward = async (s) => {
        if (s >= targetStep) return null
        const schema = EVENT_WIZARD_STEP_VALIDATION[s]
        if (schema) {
          try {
            await schema.validate(formik.values, { abortEarly: false })
          } catch (err) {
            return { failedAt: s, err }
          }
        }
        return validateForward(s + 1)
      }

      const failure = await validateForward(step)
      if (failure) {
        applyYupErrorsToFormik(formik, failure.err)
        setStep(failure.failedAt)
        toast(
          <CustomToast
            variant={alertTypes.DANGER}
            message='Please fill all the fields'
          />
        )
        return
      }
      formik.setErrors({})
      setStep(targetStep)
    },
    [step, formik, setStep]
  )

  const handlePrev = useCallback(() => {
    formik.setErrors({})
    setStep((s) => Math.max(1, s - 1))
  }, [formik, setStep])

  let submitLabel = 'Next'
  if (step === TOTAL_STEPS) {
    submitLabel = id ? 'Update' : 'Create'
  }

  return (
    <div className='event-main event-main--create'>
      <div className='container-fluid'>
        <div className='event-create-shell'>
          <div className='event-create-topbar'>
            <p className='event-create-title'>{stepTitle}</p>
            <div className='event-create-stepper-wrap'>
              <p className='event-create-step-label'>
                Step <span>{formattedStep}</span> / {totalStepsLabel}
              </p>
              <div className='event-create-progress'>
                <div
                  className='event-create-progress__fill'
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className='event-create-step-switch'>
                <Stepper
                  stepLabels={STEP_LABELS}
                  currentStep={step}
                  onPrev={handlePrev}
                  onNext={() => formik.submitForm()}
                  onStepPick={handleStepPick}
                />
              </div>
            </div>
          </div>
          <Form className='event-create-form'>
            {step === 1 ? <FirstStep /> : null}
            {step === 2 ? <LocationTimeStep /> : null}
            {step === 3 ? <AgendaStep /> : null}
            {step === 4 ? <SpeakerStep /> : null}
            {step === 5 ? <GalleryStep /> : null}

            <div className='event-create-actions'>
              <button
                type='button'
                className='event-create-btn event-create-btn--ghost'
                disabled={step === 1}
                onClick={handlePrev}
              >
                <CaretLeft size={20} className='event-create-btn__icon' />
                Previous
              </button>
              <button
                type='submit'
                className='event-create-btn event-create-btn--primary'
              >
                {submitLabel}
                <CaretRight size={20} className='event-create-btn__icon' />
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

EventWizardBody.propTypes = {
  id: PropTypes.string,
  step: PropTypes.number.isRequired,
  setStep: PropTypes.func.isRequired,
}

EventWizardBody.defaultProps = {
  id: undefined,
}

function EventCreate() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [initialValues, setInitialValues] = useState(eventInitialValues)
  const [step, setStep] = useState(1)
  const stepRef = useRef(1)

  useEffect(() => {
    stepRef.current = step
  }, [step])

  const fetchEvent = useCallback(async () => {
    const result = await showEvent(id)
    if (result?.status === 200 && result?.Event) {
      const { Event } = result
      const inferredAudience =
        Event?.audience ||
        ((Array.isArray(Event?.inviteUserIds) && Event.inviteUserIds.length > 0)
          ? 'private'
          : 'public')
      setInitialValues({
        ...Event,
        audience: inferredAudience,
      })
    }
  }, [id])

  useEffect(() => {
    if (id) {
      fetchEvent()
    }
  }, [id, fetchEvent])

  const handleCreateEvent = async (values) => {
    const mapUrl = values.location.mapUrl
    let latitude = ''
    let longitude = ''
    let match = ''

    if (mapUrl !== '' && mapUrl !== undefined) {
      if (mapUrl.includes('q=')) {
        match = mapUrl.match(/q=([-+]?\d*\.?\d+),([-+]?\d*\.?\d+)/)
      } else if (mapUrl.includes('/@')) {
        match = mapUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/)
      }
    }

    if (match) {
      latitude = match[1]
      longitude = match[2]
    }

    const payload = {
      ...values,
      location: { ...values.location, latitude, longitude },
    }

    let result
    if (id) {
      result = await updateEvent(payload, id)
    } else {
      result = await createEvent(payload)
    }

    if (result?.status === 200) {
      toast(
        <CustomToast
          variant={alertTypes.SUCCESS}
          message={result?.statusText || 'Event Created Successfully!'}
        />
      )
      navigate('/events')
    } else {
      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={result?.response?.data?.error}
        />
      )
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={EVENT_WIZARD_STEP_VALIDATION[step]}
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={async (values, { setSubmitting }) => {
        const s = stepRef.current
        if (s < TOTAL_STEPS) {
          setStep((prev) => prev + 1)
        } else {
          await handleCreateEvent(values)
        }
        setSubmitting(false)
      }}
    >
      <EventWizardBody id={id} step={step} setStep={setStep} />
    </Formik>
  )
}

export default EventCreate
